import {
  forwardRef,
  useImperativeHandle,
  Ref,
  useState,
  useCallback,
} from 'react';

import { Col, Container, Row } from 'reactstrap';

import { Modal, addToast } from '@octano/global-ui';
import ValidationFormWrapper from './ValidationFormWrapper';
import {
  CreateValidationRequest,
  CreateValidationSubmitValues,
} from '../../types';
import useTextValidationType from '../../../../components/curriculum/hooks/useTextValidationType';
import { createValidation } from '../../requests';
import { SemesterCourse } from '../../../../components/curriculum/types';
import { convertValidationToSubmit } from '../../utils';
import ValidationFormLoader from '../ValidationFormLoader';

export type ModalCreateValidationParams = {
  semesterCourse: { id?: number; name: string; code: string };
  studyPlanEnrollmentId: number;
  defaultValues?: CreateValidationSubmitValues;
  readonly?: boolean;
};

export type ModalCreateValidationSemesterCourseParams = {
  semesterCourse: SemesterCourse;
  studyPlanEnrollmentId: number;
  readonly?: boolean;
};

export type ModalCreateValidationMethods = {
  open: (semesterCourse: ModalCreateValidationParams) => void;
  openSemesterCourse: (sc: ModalCreateValidationSemesterCourseParams) => void;
  close: () => void;
};

type ModalCreateValidationProps = {
  onSave?: () => void;
};

const ModalCreateValidation = (
  { onSave }: ModalCreateValidationProps,
  ref: Ref<ModalCreateValidationMethods>,
) => {
  const { texts } = useTextValidationType();

  const [visible, setVisible] = useState(false);
  const [loading, setLoading] = useState(false);

  const [params, setParams] = useState<ModalCreateValidationParams>();

  const open = useCallback((next: ModalCreateValidationParams) => {
    setParams(next);
    setVisible(true);
  }, []);

  const openSemesterCourse = useCallback(
    (next: ModalCreateValidationSemesterCourseParams) => {
      const row = next.semesterCourse;
      open({
        semesterCourse: {
          id: row.id,
          name: row?.course?.name || '',
          code: row?.course?.code || '',
        },
        studyPlanEnrollmentId: next.studyPlanEnrollmentId,
        readonly: next.readonly,
        defaultValues: convertValidationToSubmit(row?.validation),
      });
    },
    [open],
  );

  const close = useCallback(() => {
    setVisible(false);
    setParams(undefined);
  }, []);

  useImperativeHandle(ref, () => ({ open, close, openSemesterCourse }));

  const handleSave = useCallback(
    async (values: CreateValidationSubmitValues) => {
      if (
        loading ||
        !params?.semesterCourse?.id ||
        !params?.studyPlanEnrollmentId
      ) {
        return;
      }
      const payload: CreateValidationRequest = {
        ...values,
        validationSupplementaryFilesIds:
          values?.validationSupplementaryFilesIds?.map((e) => e.id) || [],
        studyPlanEnrollmentId: params?.studyPlanEnrollmentId,
        semesterCourseId: params?.semesterCourse?.id,
      };
      setLoading(true);
      const { error } = await createValidation(payload);
      setLoading(false);
      if (error) {
        addToast({
          icon: 'error',
          color: 'danger',
          text: texts.toast.error,
        });
      } else {
        addToast({
          icon: 'success',
          color: 'success',
          text: texts.toast.success,
        });
        !!onSave && onSave();
        close();
      }
    },
    [loading, params, close, onSave, texts],
  );

  return (
    <>
      {!!visible && (
        <Modal
          size="lg"
          isOpen={true}
          toggle={() => {
            !loading && close();
          }}
        >
          <Container>
            <Row>
              <Col className="mb-5" xs={12}>
                <div className="text-black fs-22 text-center">
                  {texts.title}
                </div>
                {!params?.readonly && (
                  <div className="text-black fs-16 text-center">
                    {texts.subTitle}
                  </div>
                )}
              </Col>
            </Row>
          </Container>
          <ValidationFormLoader
            studyPlanEnrollmentId={params?.studyPlanEnrollmentId || 0}
          >
            <ValidationFormWrapper
              courseName={params?.semesterCourse?.name}
              courseCode={params?.semesterCourse?.code}
              readonly={!!params?.readonly}
              defaultValues={params?.defaultValues}
              loading={loading}
              onCancel={close}
              onConfirm={handleSave}
            />
          </ValidationFormLoader>
        </Modal>
      )}
    </>
  );
};

export default forwardRef(ModalCreateValidation);
